import { useCallback, useState } from 'react';
import UIButton from 'UIComponents/button/UIButton';
import UIIcon from 'UIComponents/icon/UIIcon';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import UIIconButton from 'UIComponents/button/UIIconButton';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { GreyContainer, LargeWrapper, PopoverHeader } from './SharedStyleComponents';
import ScreenTextArea from './ScreenTextArea';
import AIButton from 'ai-components-ui-library/components/buttons/AIButton';
import { NOT_USING_TRACKER } from 'ai-components-ui-library/hooks/useTracker';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const RewriteButton = ({
  onRefresh,
  isRefreshDisabled
}) => {
  const BreezeButton = /*#__PURE__*/_jsxs(AIButton, {
    use: "tertiary",
    size: "small",
    "data-test-id": "generate-button",
    onClick: onRefresh,
    disabled: isRefreshDisabled
    // This expects a tracker object, but we don't have one in this context
    ,
    usageTracker: NOT_USING_TRACKER,
    children: [/*#__PURE__*/_jsx(UIIcon, {
      name: "breezeRegenerate",
      size: 14
    }), /*#__PURE__*/_jsx(FormattedMessage, {
      message: "baseContentGenerationModal.inlineToolbar.previewContent.breeze.regenerate"
    })]
  });
  if (isRefreshDisabled) {
    return /*#__PURE__*/_jsx(UITooltip, {
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "baseContentGenerationModal.inlineToolbar.previewContent.rewriteDisabledTooltip"
      }),
      placement: "top",
      children: BreezeButton
    });
  }
  return BreezeButton;
};

/**
 * Adapted from
 * https://git.hubteam.com/HubSpot/Social/blob/c1ac1f9c966ad36bef6ac2146425a34e0bd41f4c/SocialUI/static/js/composer/content-assistant/PaginatorControl.tsx
 */

const NavigationButtons = ({
  onNext,
  onPrevious,
  selectedIndex,
  total
}) => {
  return /*#__PURE__*/_jsxs(Flex, {
    flexDirection: "row",
    alignItems: "center",
    marginInlineEnd: "auto",
    children: [/*#__PURE__*/_jsx(UIIconButton, {
      "data-test-id": "content-assistant-prev",
      disabled: selectedIndex === 1,
      use: "transparent",
      onClick: onPrevious,
      placement: "right",
      size: "sm",
      children: /*#__PURE__*/_jsx(UIIcon, {
        name: "left"
      })
    }), /*#__PURE__*/_jsx(Text, {
      as: "span",
      paddingInline: "200",
      "data-test-id": "content-assistant-pagination-label",
      children: `${selectedIndex} / ${total}`
    }), /*#__PURE__*/_jsx(UIIconButton, {
      "data-test-id": "content-assistant-next",
      disabled: selectedIndex === total,
      use: "transparent",
      onClick: onNext,
      placement: "right",
      size: "sm",
      children: /*#__PURE__*/_jsx(UIIcon, {
        name: "right"
      })
    })]
  });
};
export default function PreviewGeneratedContent({
  body,
  content,
  label,
  updateContent,
  onSubmit,
  onCancel,
  onRefresh,
  isRefreshDisabled,
  setShowResult,
  trackUsage,
  resultCount,
  selectedResultIndex,
  trackingProperties = {}
}) {
  const [userEditedResult, setUserEditedResult] = useState(false);
  const handleShowNextResult = useCallback(() => {
    setShowResult(selectedResultIndex + 1);
    trackUsage('preview-window-next');
  }, [selectedResultIndex, setShowResult, trackUsage]);
  const handleShowPreviousResult = useCallback(() => {
    setShowResult(selectedResultIndex - 1);
    trackUsage('preview-window-previous');
  }, [selectedResultIndex, setShowResult, trackUsage]);
  const handleRefresh = useCallback(() => {
    onRefresh();
    trackUsage('preview-window-rewrite', trackingProperties);
  }, [trackingProperties, onRefresh, trackUsage]);
  const handleSubmit = useCallback(() => {
    onSubmit();
    trackUsage('insert-content', {
      result: content.text,
      command: trackingProperties.command,
      usingBrandVoice: trackingProperties.usingBrandVoice,
      userEditedResult
    });
  }, [content, onSubmit, trackUsage, trackingProperties, userEditedResult]);
  const handleCancel = useCallback(() => {
    onCancel();
    trackUsage('preview-window-close', {
      command: trackingProperties.command
    });
  }, [onCancel, trackUsage, trackingProperties]);
  const handleUpdate = useCallback(updatedText => {
    const updatedContent = Object.assign({}, content, {
      text: updatedText
    });
    updateContent(updatedContent);
    if (!userEditedResult) {
      setUserEditedResult(true);
    }
  }, [updateContent, content, userEditedResult, setUserEditedResult]);
  return /*#__PURE__*/_jsxs(LargeWrapper, {
    children: [/*#__PURE__*/_jsx(PopoverHeader, {
      label: label,
      onCancel: handleCancel,
      body: body
    }), /*#__PURE__*/_jsx(ScreenTextArea, {
      content: content.text || '',
      updateContent: handleUpdate
    }), /*#__PURE__*/_jsxs(GreyContainer, {
      children: [/*#__PURE__*/_jsx(NavigationButtons, {
        onNext: handleShowNextResult,
        onPrevious: handleShowPreviousResult,
        total: resultCount
        // Array is 0 indexed
        ,
        selectedIndex: selectedResultIndex + 1
      }), /*#__PURE__*/_jsxs("div", {
        children: [/*#__PURE__*/_jsx(RewriteButton, {
          onRefresh: handleRefresh,
          isRefreshDisabled: isRefreshDisabled
        }), /*#__PURE__*/_jsx(UIButton, {
          size: "small",
          use: "tertiary",
          onClick: handleSubmit,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "baseContentGenerationModal.inlineToolbar.previewContent.insert"
          })
        })]
      })]
    })]
  });
}